import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { timetables } from '../data/timetables';
import TopBar from '../components/TopBar';
import SafeIcon from '../common/SafeIcon';
import ExitModal from '../components/ExitModal';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { t } = useLanguage();
  const [showExitModal, setShowExitModal] = useState(false);
  const [isAppClosed, setIsAppClosed] = useState(false);
  const isSunday = new Date().getDay() === 0;

  useEffect(() => {
    // Push an extra state to the history stack immediately to "trap" the back button
    window.history.pushState(null, null, window.location.pathname);

    const handlePopState = (event) => {
      // Re-push the state immediately to keep the user on the page
      window.history.pushState(null, null, window.location.pathname);
      // Show the exit confirmation modal
      setShowExitModal(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleExitConfirm = () => {
    setShowExitModal(false);
    
    // 1. Attempt to close the tab (works in PWA/Standalone/Windows opened by scripts)
    window.close();

    // 2. Fallback: Show the "Application Closed" screen for standard browser tabs
    // Standard browsers (Chrome/Safari) block window.close() for security unless 
    // the tab was opened via window.open()
    setTimeout(() => {
      setIsAppClosed(true);
    }, 100);
  };

  const getRouteInfo = (base) => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    let activeVariant = null;

    // Line 10 -> 10a (Sat) starting Friday 19:00
    if (base.id === 'line10') {
      if ((day === 5 && hour >= 19) || day === 6) {
        activeVariant = base.variants[6];
      }
    }
    // Line 20 -> 20a (Tue) starting Monday 19:00
    if (base.id === 'line20') {
      if ((day === 1 && hour >= 19) || day === 2) {
        activeVariant = base.variants[2];
      }
    }

    return {
      title: activeVariant ? activeVariant.title : base.title,
      operatingDays: base.id === 'line10' ? t('monSat') : t('monFri')
    };
  };

  if (isAppClosed) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-8 text-center z-[300]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="max-w-xs"
        >
          <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center mb-6 mx-auto border border-slate-800 shadow-2xl">
            <SafeIcon icon={FaIcons.FaBus} className="text-4xl text-slate-700" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Application Closed</h2>
          <p className="text-slate-400 text-sm mb-10 leading-relaxed">
            The session has ended. You can now safely close this browser tab or return to your device's home screen.
          </p>
          <button 
            onClick={() => {
              setIsAppClosed(false);
              window.location.reload();
            }}
            className="px-8 py-3 rounded-xl bg-slate-800 text-blue-400 font-bold text-sm hover:bg-slate-700 transition-colors border border-slate-700"
          >
            Re-open Application
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pb-20 bg-slate-900">
      <TopBar title={t('appTitle')} />
      
      <div className="px-4 md:px-8 lg:px-12 pt-6">
        <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-6 md:p-10 mb-8 text-white shadow-xl relative overflow-hidden border border-slate-700/50">
          <div className="relative z-10 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">{t('trackerTitle')}</h2>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">{t('trackerDesc')}</p>
          </div>
          <SafeIcon icon={FiIcons.FiMap} className="absolute -right-4 -bottom-4 text-8xl md:text-9xl text-white/5" />
        </div>

        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 px-1">
          {t('availableRoutes')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6">
          {timetables.map((timetable, index) => {
            const info = getRouteInfo(timetable);
            return (
              <Link to={`/timetable/${timetable.id}`} key={timetable.id} className="block">
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  whileHover={{ y: -2 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-slate-800 rounded-2xl p-4 md:p-5 shadow-md border border-slate-700 flex items-center space-x-4 hover:bg-slate-750 active:bg-slate-700 transition-all"
                >
                  <div className={`p-3 md:p-4 rounded-xl bg-gradient-to-br ${timetable.color} text-white shadow-sm`}>
                    <SafeIcon icon={FaIcons.FaBus} className="text-xl md:text-2xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h2 className="font-bold text-white truncate md:text-lg">{info.title}</h2>
                      <span className={`text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full uppercase ml-2 shrink-0 ${isSunday ? 'bg-red-900/30 text-red-400 border border-red-800/50' : 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/50'}`}>
                        {isSunday ? t('inactive') : t('active')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center text-[10px] md:text-xs text-slate-300 font-bold bg-slate-700/80 px-1.5 py-0.5 rounded">
                        <SafeIcon icon={FiIcons.FiCalendar} className="mr-1" />
                        {info.operatingDays}
                      </div>
                    </div>
                  </div>
                  <SafeIcon icon={FiIcons.FiChevronRight} className="text-slate-500 shrink-0" />
                </motion.div>
              </Link>
            );
          })}
        </div>
      </div>

      <ExitModal 
        isOpen={showExitModal} 
        onConfirm={handleExitConfirm} 
        onCancel={() => setShowExitModal(false)} 
      />
    </div>
  );
};

export default Home;